import React, { FC, useCallback } from 'react';

import {
    ActionIcon,
    TableHeaderCell as BaseTableHeaderCell,
    TableHeaderRow,
} from '@portal/common/components';
import { styled } from '@portal/common/theme';
import { DataCondition, DataFilters, DataKey, DataType } from '@portal/common/types';
import { localizeColumn } from '@portal/common/utility/chart-data-helpers';
import { isIE } from '@portal/common/utility/detect-browser';

import _ from '../../../locale/_';
import { getColumnWidth } from './utils';

const SortHeader = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
});

const TableHeaderCell = styled(BaseTableHeaderCell)(({ theme }) => ({
    padding: '4px 0 4px 0',
    fontSize: '1.1rem',
    [theme.breakpoint.md]: {
        padding: '4px 4px 4px 10px',
        fontSize: '1.4rem',
    },
}));

const HeaderSpacer = styled('div')(({ theme }) => ({
    width: 3,
    [theme.breakpoint.md]: {
        width: 15,
    },
}));

type Props = {
    columns: DataKey[];
    onDisplayChange: ({ sortedByColumn, sortedDescending }) => void;
    sortedByColumn: DataKey | null;
    sortedDescending: boolean;
    valueFields: string[];
    selectedRefinementFilters: DataFilters;
    selectedConditions: DataCondition[];
    dataTypes: DataType[];
};

const Headers: FC<Props> = ({
    columns,
    onDisplayChange,
    sortedByColumn,
    sortedDescending,
    selectedConditions,
    selectedRefinementFilters,
    dataTypes,
    valueFields,
}) => {
    const handleSortLabelClick = useCallback(
        (column: DataKey) => (ev) => {
            const displayState =
                sortedByColumn === column
                    ? { sortedByColumn, sortedDescending: !sortedDescending }
                    : { sortedByColumn: column, sortedDescending: true };

            onDisplayChange && onDisplayChange(displayState);
        },
        [onDisplayChange, sortedByColumn, sortedDescending]
    );

    return (
        <TableHeaderRow>
            {columns.map((column) => (
                <TableHeaderCell key={column}>
                    <SortHeader
                        onClick={handleSortLabelClick(column)}
                        style={{
                            ...(isIE() && {
                                width: getColumnWidth(
                                    columns,
                                    selectedConditions,
                                    selectedRefinementFilters,
                                    dataTypes,
                                    column
                                ),
                            }),
                        }}
                    >
                        <div
                            style={
                                valueFields.includes(column)
                                    ? {
                                          width: '100%',
                                          textAlign: 'right',
                                      }
                                    : {}
                            }
                        >
                            {localizeColumn(column, dataTypes, _)}
                        </div>
                        {sortedByColumn === column ? (
                            <ActionIcon
                                size="large"
                                src={
                                    sortedDescending
                                        ? '/icons/ic-sort-asc.svg'
                                        : '/icons/ic-sort-desc.svg'
                                }
                            />
                        ) : (
                            <HeaderSpacer />
                        )}
                    </SortHeader>
                </TableHeaderCell>
            ))}
        </TableHeaderRow>
    );
};

export default Headers;
